import React, { ReactNode } from "react";
import { i18n } from "@lingui/core";
import DateOnlyFormat from "components/DateOnlyFormat";
import { FormatDateFormat, toUTCDate } from "src/utils/date";
import {
  OfferTokenValue,
  TokenValueAmountAggregationType,
  TokenValuePercentage
} from "types/api/exclusive_offers";
import { formatCurrency } from "../../utils/localization/formatCurrency";
import { LanguageCode } from "../../utils/localization/languages/types";

const numberFormatOptions = (currency: string): Intl.NumberFormatOptions => ({
  style: "currency",
  currency: currency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const AmountPrefix = ({
  aggregationType,
  aggregatedValuesIdentical
}: {
  aggregationType: TokenValueAmountAggregationType;
  aggregatedValuesIdentical?: boolean;
}) => {
  const prefix = amountPrefix(aggregationType, aggregatedValuesIdentical);
  return prefix ? <>{prefix} </> : null;
};

const amountPrefix = (
  aggregationType: TokenValueAmountAggregationType,
  aggregatedValuesIdentical?: boolean
) => {
  if (aggregatedValuesIdentical) return null;
  if (aggregationType === "min") return i18n._({ id: "token-value-from" });
  if (aggregationType === "max") return i18n._({ id: "token-value-up-to" });
  return null;
};

const dateFormatOptions: FormatDateFormat = {
  year: "numeric",
  month: "short",
  day: "numeric"
};

function formattedValue(
  _placeholder: string,
  value: OfferTokenValue
): JSX.Element {
  switch (value.type) {
    case "date":
      return (
        <DateOnlyFormat
          value={toUTCDate(value.date as string)}
          format={dateFormatOptions}
        />
      );

    case "percentage":
      return <React.Fragment>{formatPercentage(value)}</React.Fragment>;

    case "amount":
      return (
        <React.Fragment>
          <AmountPrefix
            aggregationType={value.aggregationType}
            aggregatedValuesIdentical={value.aggregatedValuesIdentical}
          />
          {i18n.number(value.amount, numberFormatOptions(value.currency))}
        </React.Fragment>
      );
    case "string":
      return <React.Fragment>{value.value}</React.Fragment>;
    default:
      return <React.Fragment></React.Fragment>;
  }
}

export const formattedText = (
  token: OfferTokenValue,
  language: LanguageCode
): string => {
  switch (token.type) {
    case "amount":
      return [
        amountPrefix(token.aggregationType, token.aggregatedValuesIdentical),
        formatCurrency(token.amount, token.currency, language)
      ]
        .filter((x) => x)
        .join(" ");
    case "date": {
      const utcDate = toUTCDate(token.date);
      const dateFormat: Intl.DateTimeFormatOptions = {
        ...dateFormatOptions,
        timeZone: "UTC"
      };

      return new Intl.DateTimeFormat(language, dateFormat).format(utcDate);
    }
    case "percentage":
      return formatPercentage(token);
    case "string":
      return token.value;
    default:
      return "";
  }
};

const formatPercentage = (token: TokenValuePercentage): string =>
  `${token.percentage}%`;

interface PlaceholderValuesFormatterProps {
  placeholder: string;
  value: OfferTokenValue;
}

const PlaceholderValuesFormatter: React.FC<PlaceholderValuesFormatterProps> = ({
  placeholder,
  value
}) => {
  if (!value) return <React.Fragment>{placeholder}</React.Fragment>;

  return <React.Fragment>{formattedValue(placeholder, value)}</React.Fragment>;
};

export default PlaceholderValuesFormatter;

export function renderPlaceholderValue(
  placeholder: string,
  value: OfferTokenValue
): ReactNode {
  return (
    <PlaceholderValuesFormatter
      key={`${placeholder}`}
      placeholder={placeholder}
      value={value}
    />
  );
}
